import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Tags } from 'lucide-react';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { pcmpActionPlansQueryOptions } from '../../api/pcmpRequestCategories.api';
import {
  PCMP_NO_ACTION_PLAN_LABEL,
  PCMP_REQUEST_APPLIES_TO_LABELS,
} from '../../constants/pcmpRequest.constants';
import { useSavePcmpRequestCategory } from '../../hooks/useSavePcmpRequestCategory';
import {
  PCMP_ACTION_PLAN_KEYS,
  PCMP_REQUEST_APPLIES_TO,
  PCMP_REQUEST_CATEGORY_CODE_PATTERN,
  PCMP_REQUEST_CATEGORY_NAME_MAX,
  type PcmpRequestCategory,
} from '../../types/pcmpRequestCategory.types';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const NO_PLAN = '__none__';

const categoryFormSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      PCMP_REQUEST_CATEGORY_CODE_PATTERN,
      'Start with a letter; use only A-Z, 0-9 and _ (2 to 30 characters).',
    ),
  name: z
    .string()
    .trim()
    .min(1, 'Name is required.')
    .max(
      PCMP_REQUEST_CATEGORY_NAME_MAX,
      `Name must be ${PCMP_REQUEST_CATEGORY_NAME_MAX} characters or fewer.`,
    ),
  appliesTo: z.enum(PCMP_REQUEST_APPLIES_TO),
  // NO_PLAN = approving only records the decision.
  actionPlan: z.union([z.literal(NO_PLAN), z.enum(PCMP_ACTION_PLAN_KEYS)]),
  isActive: z.boolean(),
});

type CategoryFormValues = z.input<typeof categoryFormSchema>;

interface PcmpRequestCategoryDialogProps {
  /** The category to edit; null adds a new one. */
  category: PcmpRequestCategory | null;
  onClose: () => void;
}

/**
 * Legacy RequestCategories add/edit. The code is chosen once; the action plan
 * must fit the tab, and only the plans that do are offered.
 */
export function PcmpRequestCategoryDialog({ category, onClose }: PcmpRequestCategoryDialogProps) {
  const { data: plans } = useQuery(pcmpActionPlansQueryOptions());
  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      code: category?.code ?? '',
      name: category?.name ?? '',
      appliesTo: category?.appliesTo ?? 'STANDARD',
      actionPlan: category?.actionPlanKey ?? NO_PLAN,
      isActive: category?.isActive ?? true,
    },
  });
  const { mutate, isPending, error } = useSavePcmpRequestCategory(category?.id ?? null, onClose);
  const appliesTo = form.watch('appliesTo');

  const appliesToItems = PCMP_REQUEST_APPLIES_TO.map((value) => ({
    value: value as string,
    label: PCMP_REQUEST_APPLIES_TO_LABELS[value],
  }));
  const planItems = useMemo(
    () => [
      { value: NO_PLAN, label: PCMP_NO_ACTION_PLAN_LABEL },
      ...(plans ?? [])
        .filter((plan) => plan.appliesTo === appliesTo)
        .map((plan) => ({ value: plan.key as string, label: plan.label })),
    ],
    [plans, appliesTo],
  );

  const onSubmit = form.handleSubmit((values) =>
    mutate({
      code: values.code,
      name: values.name,
      appliesTo: values.appliesTo,
      actionPlanKey: values.actionPlan === NO_PLAN ? null : values.actionPlan,
      isActive: values.isActive,
    }),
  );

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={category ? 'Edit request category' : 'Add request category'}
        {...(category ? { description: category.code } : {})}
        icon={Tags}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={category ? 'Save changes' : 'Add category'}
        pendingLabel="Saving..."
      >
        {category ? null : (
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Code <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    autoComplete="off"
                    maxLength={30}
                    className="font-mono uppercase"
                    placeholder="e.g. URGENT_PAYMENT"
                  />
                </FormControl>
                <FormDescription>Cannot be changed once the category is added.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input {...field} autoComplete="off" maxLength={PCMP_REQUEST_CATEGORY_NAME_MAX} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="appliesTo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Raised from</FormLabel>
              <Select<string>
                items={appliesToItems}
                value={field.value}
                onValueChange={(next) => {
                  if (next === null) return;
                  field.onChange(next);
                  // Keep only a plan that fits the new tab.
                  const current = form.getValues('actionPlan');
                  const fits =
                    current === NO_PLAN ||
                    plans?.some((plan) => plan.key === current && plan.appliesTo === next);
                  if (!fits) form.setValue('actionPlan', NO_PLAN, { shouldDirty: true });
                }}
              >
                <FormControl>
                  <SelectTrigger aria-label="Raised from" className="w-full">
                    <SelectValue />
                    <SelectIcon />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {appliesToItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Standard: proposals Marketing approved. Disbursed: proposals Accounts paid.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="actionPlan"
          render={({ field }) => (
            <FormItem>
              <FormLabel>What approving does</FormLabel>
              <Select<string>
                items={planItems}
                value={field.value}
                onValueChange={(next) => next !== null && field.onChange(next)}
              >
                <FormControl>
                  <SelectTrigger aria-label="What approving does" className="w-full">
                    <SelectValue />
                    <SelectIcon />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {planItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex-row items-center justify-between gap-4 rounded-lg border px-4 py-3">
              <div className="flex flex-col gap-1">
                <span className="text-sm leading-none font-medium">Active</span>
                <FormDescription>Only active categories can be requested.</FormDescription>
              </div>
              <FormControl>
                <Switch
                  aria-label="Active"
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked)}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </PcmpEditDialog>
    </Form>
  );
}
